import { monthsLowerCase, monthsUpperCase } from "../../consts/forecast";
import { formatPrice } from "../common";

const monthsLong = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const csvEscape = (value) => {
    const str = String(value ?? "");
    if (/[",\n]/.test(str)) {
        return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
};

const buildCsv = (headers, rows) => {
    const csvLines = [headers, ...rows].map((cols) => cols.map(csvEscape).join(","));
    return csvLines.join("\r\n");
};

const triggerCsvDownload = (filename, csvContent) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const exportActualsVsForecastToCSV = ({ selectedForecastYear, forecastProducts, groupsBySelect, sortOrder }) => {
    const headers = [
        "Product",
        "Metric",
        "AOV",
        "Rev %",
        "YTD",
        "Annual Total",
        ...monthsLong.map((m) => `${m} ${selectedForecastYear}`),
    ];

    const rows = [];

    const sorterMap = {
        actuals_asc: (a, b) => Number(a?.annual_actual || 0) - Number(b?.annual_actual || 0),
        actuals_desc: (a, b) => Number(b?.annual_actual || 0) - Number(a?.annual_actual || 0),
        forecast_asc: (a, b) => Number(a?.annual_forecast || 0) - Number(b?.annual_forecast || 0),
        forecast_desc: (a, b) => Number(b?.annual_forecast || 0) - Number(a?.annual_forecast || 0),
        variance_asc: (a, b) => Number(a?.annual_variance || 0) - Number(b?.annual_variance || 0),
        variance_desc: (a, b) => Number(b?.annual_variance || 0) - Number(a?.annual_variance || 0),
        units_asc: (a, b) => Number(a?.annual_units || 0) - Number(b?.annual_units || 0),
        units_desc: (a, b) => Number(b?.annual_units || 0) - Number(a?.annual_units || 0),
        units_sold_asc: (a, b) => Number(a?.annual_units_sold || 0) - Number(b?.annual_units_sold || 0),
        units_sold_desc: (a, b) => Number(b?.annual_units_sold || 0) - Number(a?.annual_units_sold || 0),
        units_variance_asc: (a, b) => Number(a?.annual_units_variance || 0) - Number(b?.annual_units_variance || 0),
        units_variance_desc: (a, b) => Number(b?.annual_units_variance || 0) - Number(a?.annual_units_variance || 0),
    };
    const sorter = sorterMap?.[sortOrder] || sorterMap.actuals_desc;
    ([...(forecastProducts || [])].sort(sorter) || []).forEach((product) => {
        const aovVal = Number(product?.aov || 0);
        const pctRevVal = Number(product?.pctRev || 0);
        const productLabel = (groupsBySelect || []).find((g) => g.value === product?.id)?.label || product?.name || product?.id;

        const pushRow = (metricLabel, ytdValue, annualValue, monthlyGetter, isCurrency, includeInputs = false, isPercent = false) => {
            const annual = isCurrency ? `$${formatPrice(Number(annualValue || 0))}` : (annualValue != null ? Number(annualValue).toLocaleString("en-US") : "—");
            const ytdCell = (() => {
                if (isPercent) {
                    if (ytdValue == null) return "—";
                    const n = Number(ytdValue || 0);
                    const sign = n >= 0 ? "+" : "";
                    return `${sign}${Math.abs(n).toFixed(2)}%`;
                }
                if (isCurrency) return `$${formatPrice(Number(ytdValue || 0))}`;
                return ytdValue != null ? Number(ytdValue).toLocaleString("en-US") : "—";
            })();
            const monthCells = (monthsLowerCase || []).map((m) => {
                const v = monthlyGetter(m);
                if (isCurrency) return `$${formatPrice(Number(v || 0))}`;
                return v != null ? Number(v).toLocaleString("en-US") : "—";
            });
            rows.push([
                includeInputs ? productLabel : "",
                metricLabel,
                includeInputs ? aovVal : "",
                includeInputs ? pctRevVal : "",
                ytdCell,
                annual,
                ...monthCells,
            ]);
        };

        // Order to match UI: Actuals first; include inputs only on first row
        pushRow("Actuals", product?.ytd_actual_revenue, product?.annual_actual, (m) => product?.monthly_actuals_by_month?.[m], true, true);
        pushRow("Forecast", product?.ytd_forecast_revenue, product?.annual_forecast, (m) => product?.monthly_forecast_by_month?.[m], true);
        pushRow("Variance", product?.ytd_variance_revenue, product?.annual_variance, (m) => product?.monthly_variance_by_month?.[m], true);
        pushRow("Units", product?.ytd_forecast_units, product?.annual_units, (m) => product?.monthly_forecast_units_by_month?.[m], false);
        pushRow("Units Sold", product?.ytd_actual_units, product?.annual_units_sold, (m) => product?.monthly_units_sold_by_month?.[m], false);
        pushRow("Variance (Units)", product?.ytd_variance_percent, product?.annual_units_variance, (m) => product?.monthly_units_variance_by_month?.[m], false);
    });

    const csv = buildCsv(headers, rows);
    triggerCsvDownload(`actuals-vs-forecast-${selectedForecastYear}.csv`, csv);
};

// Internal mappers specific to Channel Breakdown export
const mapSavedToInternal = (saved) => {
    return {
        annual_actual: Number(saved?.annual?.actual || 0),
        annual_forecast: Number(saved?.annual?.forecast || 0),
        annual_variance: Number(saved?.annual?.variance ?? (Number(saved?.annual?.actual || 0) - Number(saved?.annual?.forecast || 0))),
        monthly_actuals_by_month: saved?.months?.actual || {},
        monthly_forecast_by_month: saved?.months?.forecast || {},
        monthly_variance_by_month: saved?.months?.variance || {},
    };
};

const toMonthIndex = (keyOrNumber) => {
    if (typeof keyOrNumber === "number") return keyOrNumber - 1;
    const idx = (monthsLowerCase || []).indexOf(String(keyOrNumber || "").toLowerCase());
    return idx; // -1 if not found
};

const calculateChannelProductData = (channelKey, product, planPercentages, settingsSource) => {
    const percent = (Number(planPercentages?.[channelKey]?.[product.id] || 0)) / 100;
    const settings = settingsSource?.[channelKey]?.products?.[product.id]?.settings || {};
    const startIdx = Math.max(0, toMonthIndex(settings.start_month ?? "jan") ?? 0);
    const endIdx = Math.min(11, toMonthIndex(settings.end_month ?? "dec") ?? 11);
    const isActive = (i) => i >= startIdx && i <= endIdx;

    const monthlyActuals = (monthsLowerCase || []).reduce((acc, m, i) => {
        const base = Number(product?.monthly_actuals_by_month?.[m] || 0) * percent;
        acc[m] = isActive(i) ? base : 0;
        return acc;
    }, {});
    const monthlyForecast = (monthsLowerCase || []).reduce((acc, m, i) => {
        const base = Number(product?.monthly_forecast_by_month?.[m] || 0) * percent;
        acc[m] = isActive(i) ? base : 0;
        return acc;
    }, {});
    const monthlyVariance = (monthsLowerCase || []).reduce((acc, m) => {
        acc[m] = Number(monthlyActuals[m] || 0) - Number(monthlyForecast[m] || 0);
        return acc;
    }, {});
    const annualActual = Object.values(monthlyActuals).reduce((s, v) => s + Number(v || 0), 0);
    const annualForecast = Object.values(monthlyForecast).reduce((s, v) => s + Number(v || 0), 0);
    const annualVariance = Number(annualActual || 0) - Number(annualForecast || 0);

    return {
        annual_actual: annualActual,
        annual_forecast: annualForecast,
        annual_variance: annualVariance,
        monthly_actuals_by_month: monthlyActuals,
        monthly_forecast_by_month: monthlyForecast,
        monthly_variance_by_month: monthlyVariance,
    };
};

const getChannelTotals = (channelKey, mix, planPercentages, disabledProducts, settingsSource) => {
    const baseMonthTotals = (monthsLowerCase || []).reduce((acc, m) => ({ ...acc, [m]: 0 }), {});
    const totals = {
        annual_actual: 0,
        annual_forecast: 0,
        annual_variance: 0,
        monthly_actuals_by_month: { ...baseMonthTotals },
        monthly_forecast_by_month: { ...baseMonthTotals },
        monthly_variance_by_month: { ...baseMonthTotals },
    };
    (mix || []).forEach((product) => {
        const isDisabled = Boolean(disabledProducts?.[channelKey]?.[product.id] || false);
        if (isDisabled) return;
        const data = calculateChannelProductData(channelKey, product, planPercentages, settingsSource);
        totals.annual_actual += Number(data.annual_actual || 0);
        totals.annual_forecast += Number(data.annual_forecast || 0);
        (monthsLowerCase || []).forEach((m) => {
            totals.monthly_actuals_by_month[m] += Number(data.monthly_actuals_by_month?.[m] || 0);
            totals.monthly_forecast_by_month[m] += Number(data.monthly_forecast_by_month?.[m] || 0);
        });
    });
    totals.annual_variance = totals.annual_actual - totals.annual_forecast;
    (monthsLowerCase || []).forEach((m) => {
        totals.monthly_variance_by_month[m] = Number(totals.monthly_actuals_by_month[m] || 0) - Number(totals.monthly_forecast_by_month[m] || 0);
    });
    return totals;
};

const getChannelPlanSum = (channelKey, hasUnsavedChanges, planPercentages, channelsBreakdown, disabledProducts) => {
    if (hasUnsavedChanges) {
        const entries = Object.entries(planPercentages?.[channelKey] || {});
        return entries.reduce((sum, [productId, v]) => {
            const isDisabled = Boolean(disabledProducts?.[channelKey]?.[productId] || false);
            return isDisabled ? sum : sum + Number(v || 0);
        }, 0);
    }
    const savedProducts = channelsBreakdown?.[channelKey]?.products || {};
    return Object.values(savedProducts).reduce((sum, p) => p?.disabled ? sum : sum + Number(p?.input_value || 0), 0);
};

export const exportChannelBreakdownToCSV = ({ channelKey, channelsBreakdown, hasUnsavedChanges, planPercentages, mix, groupsBySelect, disabledProducts }) => {
    const headers = [
        "Product",
        "LY %",
        "Plan %",
        "Annual",
        ...monthsUpperCase,
    ];

    const shouldUseSaved = !hasUnsavedChanges;
    const rows = [];

    (mix || []).forEach((mixItem) => {
        const isDisabled = Boolean(disabledProducts?.[channelKey]?.[mixItem.id] || channelsBreakdown?.[channelKey]?.products?.[mixItem.id]?.disabled || false);
        if (isDisabled) return;
        const savedProduct = channelsBreakdown?.[channelKey]?.products?.[mixItem.id];
        const data = (shouldUseSaved && savedProduct) ? mapSavedToInternal(savedProduct) : calculateChannelProductData(channelKey, mixItem, planPercentages, channelsBreakdown);
        const label = (groupsBySelect || []).find((group) => group.value === mixItem.id)?.label || mixItem.id;
        const planVal = Number(planPercentages?.[channelKey]?.[mixItem.id] ?? savedProduct?.input_value ?? 0);

        const monthActuals = monthsLowerCase.map((monthKey) => `$${formatPrice(Number(data?.monthly_actuals_by_month?.[monthKey] || 0))}`);
        const monthForecasts = monthsLowerCase.map((monthKey) => `$${formatPrice(Number(data?.monthly_forecast_by_month?.[monthKey] || 0))}`);
        const monthVariances = monthsLowerCase.map((monthKey) => {
            const actual = Number(data?.monthly_actuals_by_month?.[monthKey] || 0);
            const forecast = Number(data?.monthly_forecast_by_month?.[monthKey] || 0);
            return `$${formatPrice(actual - forecast)}`;
        });

        // Row 1: Actuals
        rows.push([
            label,
            "LY %",
            planVal,
            `$${formatPrice(Number(data?.annual_actual || 0))}`,
            ...monthActuals,
        ]);
        // Row 2: Forecasts
        rows.push([
            "",
            "",
            "",
            `$${formatPrice(Number(data?.annual_forecast || 0))}`,
            ...monthForecasts,
        ]);
        // Row 3: Variances
        rows.push([
            "",
            "",
            "",
            `$${formatPrice(Number(data?.annual_variance || 0))}`,
            ...monthVariances,
        ]);
    });

    // Totals rows
    const totals = shouldUseSaved && channelsBreakdown?.[channelKey]?.totals
        ? mapSavedToInternal(channelsBreakdown[channelKey].totals)
        : getChannelTotals(channelKey, mix, planPercentages, disabledProducts, channelsBreakdown);
    const sumPlan = getChannelPlanSum(channelKey, hasUnsavedChanges, planPercentages, channelsBreakdown, disabledProducts);
    const totalMonthActuals = monthsLowerCase.map((monthKey) => `$${formatPrice(Number(totals?.monthly_actuals_by_month?.[monthKey] || 0))}`);
    const totalMonthForecasts = monthsLowerCase.map((monthKey) => `$${formatPrice(Number(totals?.monthly_forecast_by_month?.[monthKey] || 0))}`);
    const totalMonthVariances = monthsLowerCase.map((monthKey) => {
        const actual = Number(totals?.monthly_actuals_by_month?.[monthKey] || 0);
        const forecast = Number(totals?.monthly_forecast_by_month?.[monthKey] || 0);
        return `$${formatPrice(actual - forecast)}`;
    });

    rows.push([
        "TOTAL",
        "LY %",
        `${Number(sumPlan || 0).toFixed(1)}%`,
        `$${formatPrice(Number(totals?.annual_actual || 0))}`,
        ...totalMonthActuals,
    ]);
    rows.push([
        "",
        "",
        "",
        `$${formatPrice(Number(totals?.annual_forecast || 0))}`,
        ...totalMonthForecasts,
    ]);
    rows.push([
        "",
        "",
        "",
        `$${formatPrice(Number(totals?.annual_variance || 0))}`,
        ...totalMonthVariances,
    ]);

    const csv = buildCsv(headers, rows);
    triggerCsvDownload(`${channelKey}-channel-breakdown.csv`, csv);
};

// Build rows for Actuals vs Forecast table (no download). Includes a title row.
const buildActualsVsForecastRowsWithTitle = ({ selectedForecastYear, forecastProducts, groupsBySelect, sortOrder }) => {
    const title = `${selectedForecastYear} YTD Revenue Actuals vs Forecast`;
    const headers = [
        "Product",
        "Metric",
        "AOV",
        "Rev %",
        "YTD",
        "Annual Total",
        ...monthsLong.map((m) => `${m} ${selectedForecastYear}`),
    ];
    const rows = [];
    const sorterMap = {
        actuals_asc: (a, b) => Number(a?.annual_actual || 0) - Number(b?.annual_actual || 0),
        actuals_desc: (a, b) => Number(b?.annual_actual || 0) - Number(a?.annual_actual || 0),
        forecast_asc: (a, b) => Number(a?.annual_forecast || 0) - Number(b?.annual_forecast || 0),
        forecast_desc: (a, b) => Number(b?.annual_forecast || 0) - Number(a?.annual_forecast || 0),
        variance_asc: (a, b) => Number(a?.annual_variance || 0) - Number(b?.annual_variance || 0),
        variance_desc: (a, b) => Number(b?.annual_variance || 0) - Number(a?.annual_variance || 0),
        units_asc: (a, b) => Number(a?.annual_units || 0) - Number(b?.annual_units || 0),
        units_desc: (a, b) => Number(b?.annual_units || 0) - Number(a?.annual_units || 0),
        units_sold_asc: (a, b) => Number(a?.annual_units_sold || 0) - Number(b?.annual_units_sold || 0),
        units_sold_desc: (a, b) => Number(b?.annual_units_sold || 0) - Number(a?.annual_units_sold || 0),
        units_variance_asc: (a, b) => Number(a?.annual_units_variance || 0) - Number(b?.annual_units_variance || 0),
        units_variance_desc: (a, b) => Number(b?.annual_units_variance || 0) - Number(a?.annual_units_variance || 0),
    };
    const sorter = sorterMap?.[sortOrder] || sorterMap.actuals_desc;
    ([...(forecastProducts || [])].sort(sorter) || []).forEach((product) => {
        const aovVal = Number(product?.aov || 0);
        const pctRevVal = Number(product?.pctRev || 0);
        const productLabel = (groupsBySelect || []).find((g) => g.value === product?.id)?.label || product?.name || product?.id;
        const pushRow = (metricLabel, ytdValue, annualValue, monthlyGetter, isCurrency, includeInputs = false, isPercent = false) => {
            const annual = isCurrency ? `$${formatPrice(Number(annualValue || 0))}` : (annualValue != null ? Number(annualValue).toLocaleString("en-US") : "—");
            const ytdCell = (() => {
                if (isPercent) {
                    if (ytdValue == null) return "—";
                    const n = Number(ytdValue || 0);
                    const sign = n >= 0 ? "+" : "";
                    return `${sign}${Math.abs(n).toFixed(2)}%`;
                }
                if (isCurrency) return `$${formatPrice(Number(ytdValue || 0))}`;
                return ytdValue != null ? Number(ytdValue).toLocaleString("en-US") : "—";
            })();
            const monthCells = (monthsLowerCase || []).map((m) => {
                const v = monthlyGetter(m);
                if (isCurrency) return `$${formatPrice(Number(v || 0))}`;
                return v != null ? Number(v).toLocaleString("en-US") : "—";
            });
            rows.push([
                includeInputs ? productLabel : "",
                metricLabel,
                includeInputs ? aovVal : "",
                includeInputs ? pctRevVal : "",
                ytdCell,
                annual,
                ...monthCells,
            ]);
        };
        // Same order as UI/CSV single export
        pushRow("Actuals", product?.ytd_actual_revenue, product?.annual_actual, (m) => product?.monthly_actuals_by_month?.[m], true, true);
        pushRow("Forecast", product?.ytd_forecast_revenue, product?.annual_forecast, (m) => product?.monthly_forecast_by_month?.[m], true);
        pushRow("Variance", product?.ytd_variance_revenue, product?.annual_variance, (m) => product?.monthly_variance_by_month?.[m], true);
        pushRow("Units", product?.ytd_forecast_units, product?.annual_units, (m) => product?.monthly_forecast_units_by_month?.[m], false);
        pushRow("Units Sold", product?.ytd_actual_units, product?.annual_units_sold, (m) => product?.monthly_units_sold_by_month?.[m], false);
        pushRow("Variance (Units)", product?.ytd_variance_percent, product?.annual_units_variance, (m) => product?.monthly_units_variance_by_month?.[m], false);
    });
    const withTitle = [[title], [], headers, ...rows, []];
    return withTitle;
};

// Build rows for Channel Breakdown table from saved data (no download). Includes a title row.
const buildChannelBreakdownRowsWithTitle = ({ channelKey, channelsBreakdown, mix, groupsBySelect }) => {
    const channelTitleMap = {
        shopify: "Shopify",
        amazon: "Amazon",
        wholesale: "Wholesale",
    };
    const title = `Channel/Product Breakdown - ${channelTitleMap[channelKey] || channelKey}`;
    const headers = [
        "Product",
        "LY %",
        "Plan %",
        "Annual",
        ...monthsUpperCase,
    ];
    const rows = [];

    const savedProducts = channelsBreakdown?.[channelKey]?.products || {};
    (mix || []).forEach((mixItem) => {
        const savedProduct = savedProducts?.[mixItem.id];
        if (!savedProduct) return;
        if (Boolean(savedProduct?.disabled)) return; // skip disabled saved products
        const data = mapSavedToInternal(savedProduct);
        const label = (groupsBySelect || []).find((group) => group.value === mixItem.id)?.label || mixItem.id;
        const planVal = Number(savedProduct?.input_value ?? 0);

        const monthActuals = monthsLowerCase.map((monthKey) => `$${formatPrice(Number(data?.monthly_actuals_by_month?.[monthKey] || 0))}`);
        const monthForecasts = monthsLowerCase.map((monthKey) => `$${formatPrice(Number(data?.monthly_forecast_by_month?.[monthKey] || 0))}`);
        const monthVariances = monthsLowerCase.map((monthKey) => {
            const actual = Number(data?.monthly_actuals_by_month?.[monthKey] || 0);
            const forecast = Number(data?.monthly_forecast_by_month?.[monthKey] || 0);
            return `$${formatPrice(actual - forecast)}`;
        });

        rows.push([
            label,
            "LY %",
            planVal,
            `$${formatPrice(Number(data?.annual_actual || 0))}`,
            ...monthActuals,
        ]);
        rows.push([
            "",
            "",
            "",
            `$${formatPrice(Number(data?.annual_forecast || 0))}`,
            ...monthForecasts,
        ]);
        rows.push([
            "",
            "",
            "",
            `$${formatPrice(Number(data?.annual_variance || 0))}`,
            ...monthVariances,
        ]);
    });

    // Totals from saved
    const savedTotals = channelsBreakdown?.[channelKey]?.totals;
    if (savedTotals) {
        const totals = mapSavedToInternal(savedTotals);
        const sumPlan = Object.values(savedProducts || {}).reduce((sum, p) => p?.disabled ? sum : sum + Number(p?.input_value || 0), 0);
        const totalMonthActuals = monthsLowerCase.map((monthKey) => `$${formatPrice(Number(totals?.monthly_actuals_by_month?.[monthKey] || 0))}`);
        const totalMonthForecasts = monthsLowerCase.map((monthKey) => `$${formatPrice(Number(totals?.monthly_forecast_by_month?.[monthKey] || 0))}`);
        const totalMonthVariances = monthsLowerCase.map((monthKey) => {
            const actual = Number(totals?.monthly_actuals_by_month?.[monthKey] || 0);
            const forecast = Number(totals?.monthly_forecast_by_month?.[monthKey] || 0);
            return `$${formatPrice(actual - forecast)}`;
        });

        rows.push([
            "TOTAL",
            "LY %",
            `${Number(sumPlan || 0).toFixed(1)}%`,
            `$${formatPrice(Number(totals?.annual_actual || 0))}`,
            ...totalMonthActuals,
        ]);
        rows.push([
            "",
            "",
            "",
            `$${formatPrice(Number(totals?.annual_forecast || 0))}`,
            ...totalMonthForecasts,
        ]);
        rows.push([
            "",
            "",
            "",
            `$${formatPrice(Number(totals?.annual_variance || 0))}`,
            ...totalMonthVariances,
        ]);
    }

    const withTitle = [[title], [], headers, ...rows, []];
    return withTitle;
};

// Export a single CSV file that includes: 1) YTD Actuals vs Forecast table, 2) Channel/Product Breakdown for Shopify, Amazon, Wholesale
export const exportAllPlanningTablesToCSV = ({ selectedForecastYear, forecastProducts, groupsBySelect, channelsBreakdown, mix, disabledProducts, sortOrder }) => {
    const allRows = [];
    // Table 1: YTD Revenue Actuals vs Forecast
    allRows.push(...buildActualsVsForecastRowsWithTitle({ selectedForecastYear, forecastProducts, groupsBySelect, sortOrder }));
    // Tables 2-4: Channel/Product Breakdown per channel (saved data)
    ["shopify", "amazon", "wholesale"].forEach((channelKey) => {
        // Build rows from saved, but skip disabled products if marked in saved
        const rows = buildChannelBreakdownRowsWithTitle({ channelKey, channelsBreakdown, mix, groupsBySelect });
        allRows.push(...rows);
    });
    const csv = allRows.map((cols) => cols.map(csvEscape).join(",")).join("\r\n");
    const filename = `planning-tables-${selectedForecastYear}.csv`;
    triggerCsvDownload(filename, csv);
};


